import React from "react";
import { authorizedFetch } from "./boot.js";

export const boxKey = box => box ? `${box.barn}:${box.number}` : "";
export const boxLabel = box => box ? `${box.barn}${box.number}` : "";

export async function request(path, input, key) {
  const response = await authorizedFetch(path, input ? {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ input, idempotency_key: key }),
  } : {});
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body?.error?.code || "request_failed");
    error.status = response.status;
    throw error;
  }
  return body;
}

// One sale and vendor's box allocation: the Boxes grid, the horse cards and the
// horse page all hold one of these. Pending work never crosses that boundary;
// no optimistic assignment or browser-persisted allocation exists.
export function useBoxAllocation({ sale, vendor, admin = false }) {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [pending, setPending] = React.useState(null);
  const [refreshRequired, setRefreshRequired] = React.useState(false);
  const mounted = React.useRef(true);
  const busyRef = React.useRef(false);
  const prefix = admin ? "admin" : "vendor";
  const readPath = `/collections/${prefix}.box_assignments.get@1/?${new URLSearchParams({ sale, vendor })}`;

  React.useEffect(() => {
    mounted.current = true;
    let active = true;
    request(readPath).then(value => { if (active) { setData(value); setLoading(false); } })
      .catch(() => { if (active) { setError("Box allocations could not be loaded. Refresh to try again."); setLoading(false); } });
    return () => { active = false; mounted.current = false; };
  }, [readPath]);

  async function refresh() {
    if (busyRef.current) return;
    setLoading(true); setError("");
    try {
      const value = await request(readPath);
      if (!mounted.current) return;
      setData(value); setRefreshRequired(false);
    } catch (_) {
      if (mounted.current) setError("The current allocation could not be loaded. Refresh before making another change.");
    } finally { if (mounted.current) setLoading(false); }
  }

  async function send(attempt) {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setError(""); setNotice(attempt.saving);
    try {
      await request(attempt.path, attempt.input, attempt.key);
      if (!mounted.current) return;
      setPending(null); setRefreshRequired(true);
      try {
        const value = await request(readPath);
        if (!mounted.current) return;
        setData(value); setRefreshRequired(false); setNotice(attempt.said);
      } catch (_) {
        if (mounted.current) setError("The change was saved, but the current boxes could not be loaded. Refresh before making another change.");
      }
    } catch (failure) {
      if (!mounted.current) return;
      setNotice("");
      if ([400, 404, 409, 412].includes(failure.status)) {
        setPending(null); setRefreshRequired(true);
        setError(failure.status === 412
          ? "The allocation or source range changed, or the box is occupied. Refresh and review before saving again."
          : "This change could not be accepted. Refresh and check the horses and boxes.");
      } else {
        setPending(attempt);
        setError("The save could not be confirmed. Retry this same save before making another change.");
      }
    } finally {
      busyRef.current = false;
      if (mounted.current) setBusy(false);
    }
  }

  const lots = data?.lots || [];
  const boxes = data?.boxes || [];
  const occupants = new Map(lots.filter(lot => lot.box).map(lot => [boxKey(lot.box), lot]));
  const locked = busy || loading || refreshRequired || Boolean(pending) || data?.canAssign !== true;
  const placeable = lots.filter(lot => !lot.withdrawn);

  // A horse leaving a box, a horse entering an empty one and a horse taking an
  // occupied one are three different governed writes. The occupied case is the
  // swap Action, never two assignments: two saves cannot be one tap.
  function choose(box, lot) {
    if (locked || !data) return;
    const sitting = occupants.get(boxKey(box)) || null;
    if (sitting && lot && sitting.id === lot.id) return;
    const tenant = admin ? { vendor } : {};
    const attempt = sitting && lot
      ? {
        path: `/actions/${prefix}.lots.swap_boxes@1/`,
        input: {
          sale, lot_id: sitting.id, other_lot_id: lot.id,
          expected_revision: sitting.revision, other_expected_revision: lot.revision,
          source_digest: data.sourceDigest, ...tenant,
        },
        saving: `Swapping Lot ${sitting.number} and Lot ${lot.number}…`,
        said: `Lot ${lot.number} is in box ${boxLabel(box)}.`,
      }
      : {
        path: `/actions/${prefix}.lots.assign_box@1/`,
        input: {
          sale, lot_id: (lot || sitting).id,
          barn: lot ? box.barn : null, box_number: lot ? box.number : null,
          expected_revision: (lot || sitting).revision,
          source_digest: data.sourceDigest, ...tenant,
        },
        saving: lot ? `Putting Lot ${lot.number} in box ${boxLabel(box)}…` : `Taking Lot ${sitting.number} out of box ${boxLabel(box)}…`,
        said: lot ? `Lot ${lot.number} is in box ${boxLabel(box)}.` : `Lot ${sitting.number} is out of box ${boxLabel(box)}.`,
      };
    attempt.key = window.crypto.randomUUID();
    setPending(attempt);
    send(attempt);
  }

  return { data, lots, boxes, occupants, placeable, error, notice, loading, busy, pending, locked, refresh, send, choose };
}
